import { Box, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { CustomButton, MessageWithIcon } from "@lubbezposrednio-frontend/commons";
import { Link, useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FiLock, FiEye, FiShield, FiUsers, FiUserPlus, FiGlobe, FiBarChart2 } from "react-icons/fi";
import { getVotingDetails, convertHtmlToText } from "@lubbezposrednio-frontend/core";
import { AdaptiveShell } from "../OrganizerDashboardView/components/AdaptiveShell";

const TYPE_LABELS: Record<string, string> = { yes_no: "TAK / NIE", single: "Jeden wybór", multiple: "Wiele wyborów" };
const ELECTORATE: Record<string, { label: string; icon: React.ElementType }> = {
  closed: { label: "Zamknięta lista", icon: FiUsers },
  signup: { label: "Zapisy (vox populi)", icon: FiUserPlus },
  open: { label: "Referendum (otwarte)", icon: FiGlobe }
};

type PlannedVoting = {
  title?: string;
  public?: boolean;
  secret?: boolean;
  electorate_type?: string;
  quorum_percent?: number | null;
  requires_verification?: boolean;
  questions_preview?: { content: string; qtype: string; answers: string[] }[];
  signup_landing_id?: string | null;
};

const SettingChip: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon, children }) => (
  <HStack spacing="6px" bg="rgba(67,0,152,0.06)" px="10px" py="5px" borderRadius="full">
    <Icon as={icon} color="primary" boxSize="13px" />
    <Text fontSize="12px" fontWeight="600" color="primary">
      {children}
    </Text>
  </HStack>
);

const VotingPreview: React.FC<{ voting: PlannedVoting }> = ({ voting }) => {
  const electorate = ELECTORATE[voting.electorate_type ?? "closed"] ?? ELECTORATE.closed;
  const questions = voting.questions_preview ?? [];

  return (
    <Box w="100%" maxW="720px" bg="white" borderRadius="18px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p={{ base: "20px", md: "26px" }} textAlign="left">
      <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color="gray.400" mb="12px">
        Podgląd głosowania
      </Text>

      <HStack spacing="8px" wrap="wrap" mb="20px">
        <SettingChip icon={FiEye}>Jawne</SettingChip>
        <SettingChip icon={FiLock}>{voting.secret ? "Tajne" : "Jawne imienne"}</SettingChip>
        <SettingChip icon={electorate.icon}>{electorate.label}</SettingChip>
        {voting.requires_verification && <SettingChip icon={FiShield}>Tylko zweryfikowani</SettingChip>}
        {voting.quorum_percent != null && <SettingChip icon={FiBarChart2}>Kworum {voting.quorum_percent}%</SettingChip>}
      </HStack>

      <VStack align="stretch" spacing="14px">
        {questions.map((q, i) => (
          <Box key={i} pt={i === 0 ? 0 : "14px"} borderTop={i === 0 ? "none" : "1px solid rgba(67,0,152,0.06)"}>
            <HStack align="flex-start" spacing="10px" mb="6px">
              <Flex w="24px" h="24px" borderRadius="full" bg="primary" color="white" align="center" justify="center" fontWeight="800" fontSize="12px" flexShrink={0}>
                {i + 1}
              </Flex>
              <Text fontWeight="700" color="primary">
                {convertHtmlToText(q.content)}
              </Text>
            </HStack>
            <Text fontSize="13px" color="gray.500" pl="34px">
              {TYPE_LABELS[q.qtype] ?? q.qtype}: {q.answers.join(" · ")}
            </Text>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export const VotingNotStartedScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation();
  const [voting, setVoting] = useState<PlannedVoting | null>(null);

  useEffect(() => {
    document.title = "Głosowanie planowane";
    if (!id) return;
    getVotingDetails(id)
      .then(res => setVoting((res?.data as { voting?: PlannedVoting })?.voting ?? null))
      .catch(() => undefined);
  }, [id]);

  const name = state?.name ?? voting?.title ?? "";
  const showPreview = voting?.public && (voting.questions_preview?.length ?? 0) > 0;

  return (
    <AdaptiveShell>
      <VStack py={{ base: "60px", md: "80px" }} px="20px" w="100%" maxW="780px" mx="auto" spacing="36px">
        <MessageWithIcon
          image="/ICON_voting.svg"
          title="Głosowanie planowane"
          message={
            <>
              <Text>Głosowanie {name} rozpocznie się</Text>
              <Text>{state?.startDate ?? ""}</Text>
            </>
          }
        />

        {voting?.signup_landing_id && (
          <Link to={`/zapis-na-glosowanie/${voting.signup_landing_id}`} tabIndex={-1}>
            <CustomButton text="Zapisz się do głosowania" backgroundColor="accent" />
          </Link>
        )}

        {showPreview && voting && <VotingPreview voting={voting} />}

        <Link to="/aktywne-glosowania" tabIndex={-1}>
          <CustomButton text="Lista głosowań" backgroundColor="primary" />
        </Link>
      </VStack>
    </AdaptiveShell>
  );
};
